import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Trash2, Loader2, Search, Download, Settings, ChevronLeft, Eye, User, Calendar, Building2, RefreshCw, CheckSquare, Square, Upload, CheckCircle2, AlertCircle, Pencil } from 'lucide-react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { generateGuestInvoicePDF } from '@/lib/generateGuestInvoicePdf';
import { hasCustomBilling, invoiceRecipient } from '@/lib/invoiceRecipient';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const fmt = (n: number) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';

const EMPTY_BILLING = {
  billingName: '', billingNifType: 'CIF', billingNif: '',
  billingAddress: '', billingEmail: '', billingIsCompany: true,
};
type BillingForm = typeof EMPTY_BILLING;

/** Datos del check-in de una reserva, ya con el override del huésped aplicado. */
function checkinData(r: any) {
  const guest = r.guest;
  const override = r.guestFormData;
  const isOwnerRes = r.codigo?.startsWith('OWN') || r.huesped?.toLowerCase() === 'propietario';
  return {
    guestName: override?.guestName || (isOwnerRes ? 'Propietario' : (guest
      ? `${guest.name} ${guest.surname}${guest.second_surname ? ' ' + guest.second_surname : ''}`.trim()
      : r.huesped || '')),
    guestDocumentType: override?.guestDocumentType ?? (guest?.document_type || ''),
    guestDocument: override?.guestDocument ?? (guest?.document_number || ''),
    guestAddress: override?.guestAddress ?? (guest
      ? [guest.residence_address, guest.residence_city, guest.residence_country].filter(Boolean).join(', ')
      : ''),
    guestNationality: override?.guestNationality ?? (guest?.nationality || ''),
  };
}

// Solo las de explotación van a Holded: las de gestión las emite el propietario
// con su NIF, así que no pertenecen a la cuenta de Sereno Tech S.L.
const isHoldedEligible = (inv: any) => inv.invoiceModel !== 'gestion' && !inv.emisorName;

/**
 * Comparador por fecha de checkout ascendente. Las fechas llegan como ISO (a
 * veces con hora), así que se recortan a YYYY-MM-DD y se comparan como texto.
 * Las que no tienen checkout van al final en vez de colarse las primeras.
 */
const byCheckoutAsc = (get: (x: any) => string | undefined) => (a: any, b: any) => {
  const da = (get(a) || '').substring(0, 10);
  const db = (get(b) || '').substring(0, 10);
  if (!da) return db ? 1 : 0;
  if (!db) return -1;
  return da.localeCompare(db);
};

function HoldedCell({ inv, onPush, pushing, disabled }: {
  inv: any; onPush: (inv: any) => void; pushing: boolean; disabled: boolean;
}) {
  if (inv.holdedId) {
    // Sin número = sigue en borrador en Holded, pendiente de aprobar allí
    const isDraft = !inv.holdedDocNumber;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-mono ${
          isDraft ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200'
        }`}
        title={inv.holdedError || (isDraft
          ? 'Enviada a Holded como borrador — apruébala allí para que reciba número'
          : `Aprobada en Holded${inv.holdedSyncedAt ? ' · enviada el ' + formatDate(inv.holdedSyncedAt) : ''}`)}>
        {inv.holdedError ? <AlertCircle size={13} className="text-red-600" /> : isDraft ? <FileText size={13} /> : <CheckCircle2 size={13} />}
        {inv.holdedDocNumber || 'Borrador'}
      </span>
    );
  }

  if (!isHoldedEligible(inv)) {
    return <span className="text-xs text-sereno-dark/30" title="Las facturas de gestión las emite el propietario">—</span>;
  }

  return (
    <div className="flex items-center gap-1.5">
      <button onClick={() => onPush(inv)} disabled={pushing || disabled}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border border-sereno-dark/25 text-sereno-dark/70 hover:bg-sereno-beau/20 hover:text-sereno-dark transition-colors disabled:opacity-50">
        {pushing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        Enviar
      </button>
      {inv.holdedError && <AlertCircle size={14} className="text-red-500" aria-label={inv.holdedError} />}
    </div>
  );
}

export default function FacturasPage() {
  // ?tab=facturas llega desde el Dashboard («Facturas de huéspedes con error de Holded»)
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<'explotacion' | 'gestion' | 'facturas'>(() => {
    const t = searchParams.get('tab');
    return t === 'gestion' || t === 'facturas' ? t : 'explotacion';
  });
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  // Shared state — reused for both explotacion and gestion tabs
  const [reservations, setReservations] = useState<any[]>([]);
  const [invoiceSeries, setInvoiceSeries] = useState<{ prefix: string; nextNum: number }>({ prefix: '', nextNum: 1 });
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [loadProgress, setLoadProgress] = useState<{ processed: number; total: number } | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const reservationsCache = useRef<Map<string, { reservations: any[]; invoiceSeries: { prefix: string; nextNum: number } }>>(new Map());
  const [creatingInvoice, setCreatingInvoice] = useState<number | null>(null);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<any>>(new Set());
  const [filterPending, setFilterPending] = useState(false);
  const [filterOwnerRes, setFilterOwnerRes] = useState('');

  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterMonth, setFilterMonth] = useState(0);
  const [filterOwner, setFilterOwner] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  const [bulkDeletingInvoices, setBulkDeletingInvoices] = useState(false);
  const [pushingHolded, setPushingHolded] = useState<string | null>(null);
  const [bulkPushing, setBulkPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState<{ done: number; total: number } | null>(null);

  const [showSeriesSettings, setShowSeriesSettings] = useState(false);
  const [seriesPrefix, setSeriesPrefix] = useState('');
  const [seriesNextNum, setSeriesNextNum] = useState('');
  const [savingSeries, setSavingSeries] = useState(false);

  const [guestModal, setGuestModal] = useState<{ reservation: any } | null>(null);
  const [guestForm, setGuestForm] = useState({ guestName: '', guestDocumentType: 'DNI', guestDocument: '', guestAddress: '', guestNationality: '' });
  const [billingModal, setBillingModal] = useState<{ reservation: any } | null>(null);
  const [billingForm, setBillingForm] = useState<BillingForm>({ ...EMPTY_BILLING });
  const [creatingFromModal, setCreatingFromModal] = useState(false);
  const [downloadingPdfRow, setDownloadingPdfRow] = useState<number | null>(null);

  const fetchReservations = useCallback((m: number, y: number, model: 'explotacion' | 'gestion') => {
    const cacheKey = `${model}-${m}-${y}`;
    const cached = reservationsCache.current.get(cacheKey);
    if (cached) {
      setReservations(cached.reservations);
      setInvoiceSeries(cached.invoiceSeries);
      setLoadingReservations(false);
      setLoadProgress(null);
      return;
    }

    // Cancel any in-flight stream
    streamAbortRef.current?.abort();
    const ctrl = new AbortController();
    streamAbortRef.current = ctrl;

    setLoadingReservations(true);
    setReservations([]);
    setLoadProgress(null);
    setSelectedIds(new Set());
    setFilterOwnerRes('');
    setFilterPending(false);

    const collected: any[] = [];
    api.streamInvoiceReservations(
      m, y,
      (r) => { collected.push(r); setReservations(prev => [...prev, r]); },
      (p) => setLoadProgress(p),
      ({ invoiceSeries: s }) => {
        const series = s || { prefix: `F-${y}-`, nextNum: 1 };
        setInvoiceSeries(series);
        reservationsCache.current.set(cacheKey, { reservations: collected, invoiceSeries: series });
        setLoadingReservations(false);
        setLoadProgress(null);
      },
      ctrl.signal,
      model,
    ).catch(err => {
      if (err?.name !== 'AbortError') console.error('Error fetching reservations:', err);
      setLoadingReservations(false);
      setLoadProgress(null);
    });
  }, []);

  // Reload reservations when month/year changes (uses current active model tab)
  useEffect(() => {
    if (tab === 'explotacion' || tab === 'gestion') {
      fetchReservations(month, year, tab);
    }
    return () => streamAbortRef.current?.abort();
  }, [month, year, tab]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'facturas' && invoices.length === 0) loadInvoices();
    if (tab !== 'facturas') setSelectedInvoiceIds(new Set());
  }, [tab]); // eslint-disable-line

  async function loadInvoices() {
    setLoadingInvoices(true);
    try {
      const data = await api.getInvoices();
      setInvoices(data);
    } catch (err) {
      console.error('Error loading invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  }

  function buildInvoiceData(reservation: any) {
    const nights = Math.ceil(
      (new Date(reservation.salida).getTime() - new Date(reservation.entrada).getTime()) / 86400000
    );
    return {
      ownerId: reservation.ownerId,
      ownerName: reservation.ownerName,
      propertyNickname: reservation.anuncio,
      invoiceModel: tab === 'gestion' ? 'gestion' : 'explotacion',
      ...checkinData(reservation),
      // Si el cliente dio sus propios datos de facturación, la factura sale a
      // su nombre; el huésped se conserva como dato de la estancia
      ...(reservation.billingFormData || {}),
      checkIn: reservation.entrada,
      checkOut: reservation.salida,
      nights,
      guests: reservation.huespedes,
      channel: reservation.canal,
      totalAmount: reservation.precioTotal,
      cleaningFee: reservation.limpieza,
      hostifyReservationId: reservation.id,
      checkinId: reservation.checkinId,
      periodo: `${MONTHS[month - 1]} ${year}`,
    };
  }

  async function handleCreateInvoice(reservation: any) {
    setCreatingInvoice(reservation.id);
    try {
      const invoice = await api.createInvoice(buildInvoiceData(reservation));
      setReservations(prev => prev.map(r => r.id === reservation.id ? { ...r, hasInvoice: true, invoice } : r));
      setInvoices(prev => [invoice, ...prev]);
      // Update cache entry so switching tabs shows correct hasInvoice state
      const cacheKey = `${tab}-${month}-${year}`;
      const cached = reservationsCache.current.get(cacheKey);
      if (cached) reservationsCache.current.set(cacheKey, {
        ...cached,
        reservations: cached.reservations.map(r => r.id === reservation.id ? { ...r, hasInvoice: true, invoice } : r),
      });
    } catch (err: any) {
      alert('Error creando factura: ' + (err.message || err));
    } finally {
      setCreatingInvoice(null);
    }
  }

  async function handleBulkCreate(reservationList: any[]) {
    if (reservationList.length === 0) return;
    setBulkCreating(true);
    setBulkProgress({ done: 0, total: reservationList.length });
    const errors: string[] = [];
    const cacheKey = `${tab}-${month}-${year}`;
    for (let i = 0; i < reservationList.length; i++) {
      const r = reservationList[i];
      try {
        const invoice = await api.createInvoice(buildInvoiceData(r));
        setReservations(prev => prev.map(x => x.id === r.id ? { ...x, hasInvoice: true } : x));
        setInvoices(prev => [invoice, ...prev]);
        // Update cache entry
        const cached = reservationsCache.current.get(cacheKey);
        if (cached) reservationsCache.current.set(cacheKey, {
          ...cached,
          reservations: cached.reservations.map(x => x.id === r.id ? { ...x, hasInvoice: true } : x),
        });
      } catch (err: any) {
        errors.push(`${r.anuncio}: ${err.message || err}`);
      }
      setBulkProgress({ done: i + 1, total: reservationList.length });
    }
    setBulkCreating(false);
    setBulkProgress(null);
    setSelectedIds(new Set());
    if (errors.length > 0) alert(`${errors.length} facturas fallaron:\n${errors.join('\n')}`);
  }

  async function handleDownloadPdf(invoice: any) {
    setDownloadingPdf(invoice.id);
    try { generateGuestInvoicePDF(invoice); } finally { setDownloadingPdf(null); }
  }

  function applyInvoiceUpdate(updated: any) {
    setInvoices(prev => prev.map(i => i.id === updated.id ? updated : i));
    setSelectedInvoice((cur: any) => cur?.id === updated.id ? updated : cur);
  }

  async function handlePushToHolded(invoice: any) {
    setPushingHolded(invoice.id);
    try {
      applyInvoiceUpdate(await api.pushInvoiceToHolded(invoice.id));
    } catch (err: any) {
      alert('Error enviando a Holded: ' + (err.message || err));
    } finally {
      setPushingHolded(null);
    }
  }

  async function handleBulkPushToHolded() {
    const pending = filteredInvoices.filter(
      (i: any) => selectedInvoiceIds.has(i.id) && isHoldedEligible(i) && !i.holdedId
    );
    if (pending.length === 0) return;
    if (!confirm(`¿Enviar ${pending.length} factura${pending.length === 1 ? '' : 's'} a Holded?`)) return;

    setBulkPushing(true);
    setPushProgress({ done: 0, total: pending.length });
    const errors: string[] = [];
    const sent = new Set<string>();

    for (let i = 0; i < pending.length; i++) {
      try {
        applyInvoiceUpdate(await api.pushInvoiceToHolded(pending[i].id));
        sent.add(pending[i].id);
      } catch (err: any) {
        errors.push(`${pending[i].invoiceNumber}: ${err.message || err}`);
      }
      setPushProgress({ done: i + 1, total: pending.length });
    }

    // Deja seleccionadas solo las que fallaron, para poder reintentar
    setSelectedInvoiceIds(prev => new Set([...prev].filter(id => !sent.has(id))));
    setBulkPushing(false);
    setPushProgress(null);
    if (errors.length > 0) alert(`${errors.length} facturas no se enviaron:\n${errors.join('\n')}`);
  }

  async function handleDeleteInvoice(invoice: any) {
    const id = typeof invoice === 'string' ? invoice : invoice.id;
    const inHolded = typeof invoice !== 'string' && invoice.holdedId;
    const warning = inHolded
      ? `Esta factura ya está en Holded (${invoice.holdedDocNumber || invoice.holdedId}). Al borrarla aquí el documento seguirá existiendo allí.\n\n¿Eliminarla del CRM igualmente?`
      : '¿Eliminar esta factura?';
    if (!confirm(warning)) return;
    try {
      await api.deleteInvoice(id, !!inHolded);
      setInvoices(prev => prev.filter(i => i.id !== id));
      setSelectedInvoiceIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      if (selectedInvoice?.id === id) setSelectedInvoice(null);
    } catch (err: any) {
      alert('Error eliminando factura: ' + (err.message || err));
    }
  }

  async function handleBulkDeleteInvoices() {
    if (selectedInvoiceIds.size === 0) return;
    const inHolded = invoices.filter((i: any) => selectedInvoiceIds.has(i.id) && i.holdedId).length;
    const warning = inHolded > 0
      ? `${inHolded} de las ${selectedInvoiceIds.size} facturas ya están en Holded y no se borrarán de allí.\n\n¿Eliminar las ${selectedInvoiceIds.size} del CRM? Esta acción no se puede deshacer.`
      : `¿Eliminar ${selectedInvoiceIds.size} factura${selectedInvoiceIds.size === 1 ? '' : 's'}? Esta acción no se puede deshacer.`;
    if (!confirm(warning)) return;
    setBulkDeletingInvoices(true);
    const ids = Array.from(selectedInvoiceIds);
    const failed: string[] = [];
    const reasons: string[] = [];
    for (const id of ids) {
      try {
        await api.deleteInvoice(id, true);
        setInvoices(prev => prev.filter(i => i.id !== id));
      } catch (err: any) {
        failed.push(id);
        const num = invoices.find((i: any) => i.id === id)?.invoiceNumber || id;
        reasons.push(`${num}: ${err.message || err}`);
      }
    }
    setSelectedInvoiceIds(new Set(failed));
    if (selectedInvoice && ids.includes(selectedInvoice.id) && !failed.includes(selectedInvoice.id)) setSelectedInvoice(null);
    setBulkDeletingInvoices(false);
    if (failed.length > 0) alert(`No se pudieron eliminar ${failed.length} facturas:\n${reasons.join('\n')}`);
  }

  async function handleSaveSeries() {
    setSavingSeries(true);
    try {
      const updated = await api.updateInvoiceSeries({ prefix: seriesPrefix, nextNum: Number(seriesNextNum) });
      setInvoiceSeries(updated);
      setShowSeriesSettings(false);
    } catch (err: any) {
      alert('Error guardando serie: ' + (err.message || err));
    } finally {
      setSavingSeries(false);
    }
  }

  function openSeriesSettings() {
    setSeriesPrefix(invoiceSeries.prefix || `F-${year}-`);
    setSeriesNextNum(String(invoiceSeries.nextNum || 1));
    setShowSeriesSettings(true);
  }

  function openGuestModal(r: any) {
    const data = checkinData(r);
    setGuestForm({ ...data, guestDocumentType: data.guestDocumentType || 'DNI' });
    setGuestModal({ reservation: r });
  }

  /**
   * Guarda en la reserva los datos con los que se facturará. Solo viven en el
   * cliente hasta que se emite la factura, así que también van a la caché para
   * que sobrevivan a un cambio de pestaña.
   */
  function patchReservation(rid: any, patch: Record<string, any>) {
    const apply = (r: any) => r.id === rid ? { ...r, ...patch } : r;
    setReservations(prev => prev.map(apply));
    const cacheKey = `${tab}-${month}-${year}`;
    const cached = reservationsCache.current.get(cacheKey);
    if (cached) {
      reservationsCache.current.set(cacheKey, { ...cached, reservations: cached.reservations.map(apply) });
    }
  }

  function saveGuestData() {
    if (!guestModal) return;
    patchReservation(guestModal.reservation.id, { guestFormData: { ...guestForm } });
    setGuestModal(null);
  }

  function openBillingModal(r: any) {
    // En blanco a propósito cuando aún no hay datos propios: los que da el
    // cliente son otros, y arrastrar los del check-in acaba mezclando la
    // dirección del huésped con el nombre de su empresa
    setBillingForm(r.billingFormData ? { ...EMPTY_BILLING, ...r.billingFormData } : { ...EMPTY_BILLING });
    setBillingModal({ reservation: r });
  }

  /** Vuelca los datos del check-in en el formulario, para retocar solo lo que cambia. */
  function copyCheckinIntoBilling() {
    if (!billingModal) return;
    const data = checkinData(billingModal.reservation);
    // Copiar el check-in siempre trae a una persona, así que el documento es
    // el suyo y nunca un CIF
    setBillingForm(f => ({
      ...f,
      billingName: data.guestName,
      billingNif: data.guestDocument,
      billingNifType: data.guestDocumentType || 'DNI',
      billingAddress: data.guestAddress,
      billingIsCompany: false,
    }));
  }

  /**
   * Facturar a otro nombre exige datos fiscales completos: nombre o razón
   * social, NIF/CIF y dirección. A medias no sirven ni para el PDF ni para
   * Holded, así que o están los tres o no se guarda nada.
   */
  function billingPayload(form: BillingForm) {
    const name = form.billingName.trim();
    const nif = form.billingNif.trim();
    const address = form.billingAddress.trim();
    if (!name || !nif || !address) return null;
    return {
      billingName: name,
      billingNif: nif,
      billingNifType: form.billingNifType,
      billingAddress: address,
      billingEmail: form.billingEmail.trim(),
      billingIsCompany: form.billingIsCompany,
    };
  }

  function saveBillingData() {
    if (!billingModal) return;
    patchReservation(billingModal.reservation.id, { billingFormData: billingPayload(billingForm) });
    setBillingModal(null);
  }

  /** Deja la reserva como estaba: se facturará al huésped del check-in. */
  function clearBillingData() {
    if (!billingModal) return;
    patchReservation(billingModal.reservation.id, { billingFormData: null });
    setBillingModal(null);
  }

  async function handleBillingModalInvoice() {
    if (!billingModal) return;
    const billingFormData = billingPayload(billingForm);
    const updated = { ...billingModal.reservation, billingFormData };
    patchReservation(billingModal.reservation.id, { billingFormData });
    setCreatingFromModal(true);
    setBillingModal(null);
    try {
      await handleCreateInvoice(updated);
    } finally {
      setCreatingFromModal(false);
    }
  }

  async function handleGuestModalInvoice() {
    if (!guestModal) return;
    const updatedReservation = { ...guestModal.reservation, guestFormData: { ...guestForm } };
    setCreatingFromModal(true);
    setGuestModal(null);
    try {
      await handleCreateInvoice(updatedReservation);
    } finally {
      setCreatingFromModal(false);
    }
  }

  async function handleDownloadPdfRow(r: any) {
    if (r.invoice) {
      setDownloadingPdfRow(r.id);
      try { generateGuestInvoicePDF(r.invoice); } finally { setDownloadingPdfRow(null); }
      return;
    }
    const inv = invoices.find((i: any) => i.hostifyReservationId === r.id);
    if (inv) { generateGuestInvoicePDF(inv); return; }
    setDownloadingPdfRow(r.id);
    try {
      const all = await api.getInvoices();
      setInvoices(all);
      const found = all.find((i: any) => i.hostifyReservationId === r.id);
      if (found) { generateGuestInvoicePDF(found); return; }
      alert('No se puede descargar el PDF. Busca la factura en "Facturas emitidas".');
    } finally {
      setDownloadingPdfRow(null);
    }
  }

  const ownerNamesRes = [...new Set(reservations.map((r: any) => r.ownerName).filter(Boolean))].sort() as string[];
  let filteredReservations = filterOwnerRes ? reservations.filter((r: any) => r.ownerName === filterOwnerRes) : reservations;
  if (filterPending) filteredReservations = filteredReservations.filter((r: any) => !r.hasInvoice);
  // Por checkout ascendente: el mes se recorre de principio a fin. Copia previa
  // porque las reservas llegan por streaming y sort() muta el array de estado.
  filteredReservations = [...filteredReservations].sort(byCheckoutAsc(r => r.salida));
  const pendingCount = reservations.filter((r: any) => !r.hasInvoice && (filterOwnerRes ? r.ownerName === filterOwnerRes : true)).length;

  // Reservations that can be bulk-invoiced: not yet invoiced + have guest
  // document data, o los datos de facturación que dio el cliente
  const invoiceable = filteredReservations.filter(
    (r: any) => !r.hasInvoice && (r.guest?.document_number || r.billingFormData?.billingNif)
  );
  const selectedList = filteredReservations.filter((r: any) => selectedIds.has(r.id) && !r.hasInvoice);

  function toggleSelect(id: any) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    const pendingIds = filteredReservations.filter((r: any) => !r.hasInvoice).map((r: any) => r.id);
    const allSelected = pendingIds.every((id: any) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(pendingIds));
  }

  const filteredInvoices = invoices.filter((inv: any) => {
    if (filterOwner && inv.ownerId !== filterOwner) return false;
    if (filterYear && inv.periodo) {
      const parts = inv.periodo.split(' ');
      if (Number(parts[1]) !== filterYear) return false;
      if (filterMonth > 0 && MONTHS.indexOf(parts[0]) + 1 !== filterMonth) return false;
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return inv.guestName?.toLowerCase().includes(s) || inv.invoiceNumber?.toLowerCase().includes(s) || inv.propertyNickname?.toLowerCase().includes(s);
    }
    return true;
  }).sort(byCheckoutAsc(inv => inv.checkOut));

  const uniqueOwners = [...new Map(invoices.map((i: any) => [i.ownerId, { id: i.ownerId, name: i.ownerName }])).values()];

  // De lo seleccionado, lo que realmente se puede empujar: explotación y sin enviar
  const pushableCount = filteredInvoices.filter(
    (i: any) => selectedInvoiceIds.has(i.id) && isHoldedEligible(i) && !i.holdedId
  ).length;

  if (selectedInvoice) {
    const inv = selectedInvoice;
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedInvoice(null)} className="p-2 rounded-lg hover:bg-sereno-beau/30 text-sereno-dark/50">
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-sereno-dark">Factura {inv.invoiceNumber}</h1>
            <p className="text-sm text-sereno-dark/50 mt-0.5">{inv.periodo}</p>
          </div>
          <div className="flex gap-2">
            {inv.holdedId ? (
              inv.holdedDocNumber ? (
                <span className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                  <CheckCircle2 size={16} />
                  Holded {inv.holdedDocNumber}
                </span>
              ) : (
                <span className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm"
                  title="Apruébala en Holded para que reciba número">
                  <FileText size={16} />
                  Borrador en Holded
                </span>
              )
            ) : isHoldedEligible(inv) && (
              <button onClick={() => handlePushToHolded(inv)} disabled={pushingHolded === inv.id}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-sereno-dark/30 text-sereno-dark hover:bg-sereno-beau/20 transition-colors disabled:opacity-50">
                {pushingHolded === inv.id ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                Enviar a Holded
              </button>
            )}
            <button onClick={() => handleDownloadPdf(inv)} disabled={downloadingPdf === inv.id}
              className="flex items-center gap-2 px-4 py-2 bg-sereno-dark text-white rounded-lg hover:bg-sereno-dark/90 transition-colors disabled:opacity-50">
              {downloadingPdf === inv.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Descargar PDF
            </button>
            <button onClick={() => handleDeleteInvoice(inv)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
              <Trash2 size={16} /> Eliminar
            </button>
          </div>
        </div>

        {inv.holdedError && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{inv.holdedError}</span>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 bg-gray-50 border-b flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-sereno-dark">Sereno Tech S.L.</p>
              <p className="text-sm text-sereno-dark/60">CIF: B44916658 · Calle Tulipán, 1 · Barbate</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-lg font-bold text-sereno-dark">{inv.invoiceNumber}</p>
              <p className="text-sm text-sereno-dark/60">{formatDate(inv.invoiceDate)}</p>
            </div>
          </div>
          {hasCustomBilling(inv) && (() => {
            const cliente = invoiceRecipient(inv);
            return (
              <div className="px-6 py-4 border-b bg-blue-50/40">
                <h3 className="text-xs uppercase tracking-wider text-blue-700/70 font-semibold mb-2">
                  Datos de facturación {cliente.isCompany ? '(empresa)' : '(particular)'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-sereno-dark/50">{cliente.isCompany ? 'Razón social' : 'Nombre'}:</span> <span className="font-medium">{cliente.name}</span></div>
                  <div><span className="text-sereno-dark/50">{cliente.documentType}:</span> <span className="font-medium">{cliente.document || '—'}</span></div>
                  {cliente.address && <div className="md:col-span-2"><span className="text-sereno-dark/50">Dirección fiscal:</span> <span className="font-medium">{cliente.address}</span></div>}
                  {cliente.email && <div className="md:col-span-2"><span className="text-sereno-dark/50">Email:</span> <span className="font-medium">{cliente.email}</span></div>}
                </div>
              </div>
            );
          })()}

          <div className="px-6 py-4 border-b">
            <h3 className="text-xs uppercase tracking-wider text-sereno-dark/40 font-semibold mb-2">Datos del huésped</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-sereno-dark/50">Nombre:</span> <span className="font-medium">{inv.guestName}</span></div>
              <div><span className="text-sereno-dark/50">{inv.guestDocumentType || 'Documento'}:</span> <span className="font-medium">{inv.guestDocument || '—'}</span></div>
              {inv.guestAddress && <div className="md:col-span-2"><span className="text-sereno-dark/50">Dirección:</span> <span className="font-medium">{inv.guestAddress}</span></div>}
              {inv.guestNationality && <div><span className="text-sereno-dark/50">Nacionalidad:</span> <span className="font-medium">{inv.guestNationality}</span></div>}
            </div>
          </div>
          <div className="px-6 py-4 border-b">
            <h3 className="text-xs uppercase tracking-wider text-sereno-dark/40 font-semibold mb-2">Estancia</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-sereno-dark/50 block">Propiedad</span><span className="font-medium">{inv.propertyNickname}</span></div>
              <div><span className="text-sereno-dark/50 block">Check-in</span><span className="font-medium">{formatDate(inv.checkIn)}</span></div>
              <div><span className="text-sereno-dark/50 block">Check-out</span><span className="font-medium">{formatDate(inv.checkOut)}</span></div>
              <div><span className="text-sereno-dark/50 block">Noches</span><span className="font-medium">{inv.nights}</span></div>
            </div>
          </div>
          <div className="px-6 py-4">
            <h3 className="text-xs uppercase tracking-wider text-sereno-dark/40 font-semibold mb-3">Desglose</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 text-left font-medium text-sereno-dark/60">Concepto</th>
                  <th className="pb-2 text-right font-medium text-sereno-dark/60">Base</th>
                  <th className="pb-2 text-right font-medium text-sereno-dark/60">IVA 10%</th>
                  <th className="pb-2 text-right font-medium text-sereno-dark/60">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2">Alojamiento ({inv.nights} noches)</td>
                  <td className="py-2 text-right tabular-nums">{fmt(inv.accommodationBase)}</td>
                  <td className="py-2 text-right tabular-nums">{fmt(inv.accommodationIva)}</td>
                  <td className="py-2 text-right tabular-nums font-medium">{fmt(inv.accommodationTotal)}</td>
                </tr>
                {inv.cleaningFee > 0 && (
                  <tr>
                    <td className="py-2">Gastos de limpieza</td>
                    <td className="py-2 text-right tabular-nums">{fmt(inv.cleaningBase)}</td>
                    <td className="py-2 text-right tabular-nums">{fmt(inv.cleaningIva)}</td>
                    <td className="py-2 text-right tabular-nums font-medium">{fmt(inv.cleaningFee)}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-sereno-dark/20">
                  <td className="pt-3 font-bold text-sereno-dark" colSpan={2}>TOTAL</td>
                  <td className="pt-3 text-right text-xs text-sereno-dark/50">IVA: {fmt(inv.ivaAmount)}</td>
                  <td className="pt-3 text-right tabular-nums text-lg font-bold text-sereno-dark">{fmt(inv.totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
            <p className="text-xs text-sereno-dark/40 mt-4">* IVA al tipo reducido del 10% — Servicios de hospedaje (Ley 37/1992 art. 91.Uno.2.2.º)</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-sereno-dark">Facturas a Huéspedes</h1>
          <p className="text-sm text-sereno-dark/50 mt-1">Facturas emitidas por alojamiento turístico</p>
        </div>
        <button onClick={openSeriesSettings}
          className={`self-start sm:self-auto px-3 py-2 rounded-lg text-sereno-dark/70 hover:bg-sereno-beau/30 transition-colors flex items-center gap-1.5 text-sm ${tab !== 'explotacion' ? 'invisible pointer-events-none' : ''}`}>
          <Settings size={16} /> Serie: <span className="font-mono font-medium">{invoiceSeries.prefix}{String(invoiceSeries.nextNum || 1).padStart(4, '0')}</span>
        </button>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        <button onClick={() => setTab('explotacion')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'explotacion' ? 'border-sereno-dark text-sereno-dark' : 'border-transparent text-sereno-dark/50 hover:text-sereno-dark'}`}>
          Facturas de explotación
          {tab === 'explotacion' && pendingCount > 0 && <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{pendingCount}</span>}
        </button>
        <button onClick={() => setTab('gestion')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'gestion' ? 'border-sereno-dark text-sereno-dark' : 'border-transparent text-sereno-dark/50 hover:text-sereno-dark'}`}>
          Facturas de gestión
          {tab === 'gestion' && pendingCount > 0 && <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{pendingCount}</span>}
        </button>
        <button onClick={() => setTab('facturas')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'facturas' ? 'border-sereno-dark text-sereno-dark' : 'border-transparent text-sereno-dark/50 hover:text-sereno-dark'}`}>
          Facturas emitidas
          {invoices.length > 0 && <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{invoices.length}</span>}
        </button>
      </div>

      {(tab === 'explotacion' || tab === 'gestion') && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select className="px-3 py-2 border rounded-lg text-sm" value={month} onChange={e => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="px-3 py-2 border rounded-lg text-sm" value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => {
                reservationsCache.current.delete(`${tab}-${month}-${year}`);
                fetchReservations(month, year, tab as 'explotacion' | 'gestion');
              }} disabled={loadingReservations}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-sereno-dark/70 hover:bg-sereno-beau/20 disabled:opacity-50 transition-colors">
              {loadingReservations ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Actualizar
            </button>
            {ownerNamesRes.length > 1 && (
              <select className="px-3 py-2 border rounded-lg text-sm" value={filterOwnerRes} onChange={e => setFilterOwnerRes(e.target.value)}>
                <option value="">Todos los propietarios</option>
                {ownerNamesRes.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
            <button onClick={() => setFilterPending(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${filterPending ? 'bg-amber-50 border-amber-300 text-amber-700' : 'text-sereno-dark/70 hover:bg-sereno-beau/20'}`}>
              {filterPending ? <CheckSquare size={14} /> : <Square size={14} />}
              Solo pendientes
            </button>
          </div>

          {/* Bulk action bar — shown when there are pending reservations */}
          {!loadingReservations && filteredReservations.some((r: any) => !r.hasInvoice) && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-sereno-beau/20 rounded-xl border border-sereno-beau/50">
              <button onClick={toggleSelectAll}
                className="flex items-center gap-1.5 text-sm text-sereno-dark/70 hover:text-sereno-dark transition-colors">
                {filteredReservations.filter((r: any) => !r.hasInvoice).every((r: any) => selectedIds.has(r.id))
                  ? <CheckSquare size={16} className="text-sereno-dark" />
                  : <Square size={16} />}
                Seleccionar todas las pendientes
              </button>
              {selectedList.length > 0 && (
                <>
                  <span className="text-xs text-sereno-dark/50">{selectedList.length} seleccionadas</span>
                  <button onClick={() => handleBulkCreate(selectedList)} disabled={bulkCreating}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sereno-dark text-white text-sm hover:bg-sereno-dark/90 disabled:opacity-50 transition-colors">
                    {bulkCreating ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                    Facturar seleccionadas ({selectedList.length})
                  </button>
                </>
              )}
              {invoiceable.length > 0 && (
                <button onClick={() => handleBulkCreate(invoiceable)} disabled={bulkCreating}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sereno-dark text-sereno-dark text-sm hover:bg-sereno-dark/10 disabled:opacity-50 transition-colors ml-auto">
                  {bulkCreating ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  Facturar todo con datos ({invoiceable.length})
                </button>
              )}
              {bulkProgress && (
                <div className="w-full space-y-1">
                  <div className="flex justify-between text-xs text-sereno-dark/50">
                    <span>Generando facturas…</span>
                    <span className="tabular-nums">{bulkProgress.done} / {bulkProgress.total}</span>
                  </div>
                  <div className="h-1.5 bg-sereno-beau/40 rounded-full overflow-hidden">
                    <div className="h-full bg-sereno-dark/60 rounded-full transition-all duration-200"
                      style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inline progress bar while streaming */}
          {loadingReservations && (
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
              <div className="flex items-center gap-3">
                <Loader2 size={16} className="animate-spin text-sereno-dark/40 flex-shrink-0" />
                {loadProgress && loadProgress.total > 0 ? (
                  <div className="flex-1 flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-sereno-dark/40 rounded-full transition-all duration-300"
                        style={{ width: `${Math.round((loadProgress.processed / loadProgress.total) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs tabular-nums text-sereno-dark/50 flex-shrink-0">{loadProgress.processed} / {loadProgress.total}</span>
                  </div>
                ) : (
                  <span className="text-sm text-sereno-dark/50">Cargando reservas…</span>
                )}
              </div>
            </div>
          )}

          {!loadingReservations && filteredReservations.length === 0 ? (
            <div className="text-center py-20 text-sereno-dark/40">
              <Calendar size={48} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg">No hay reservas en {MONTHS[month - 1]} {year}</p>
              <p className="text-sm mt-1">
                {tab === 'explotacion'
                  ? 'Solo se muestran propiedades en régimen de explotación.'
                  : 'Solo se muestran propiedades en régimen de gestión.'}
              </p>
            </div>
          ) : filteredReservations.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b text-sm text-sereno-dark/60">
                <span className="font-medium text-sereno-dark">{filteredReservations.length}</span> reservas en <span className="font-medium text-sereno-dark">{MONTHS[month - 1]} {year}</span>
                {' · '}<span className="text-amber-600 font-medium">{pendingCount} pendientes</span>
                {' · '}<span className="text-green-600 font-medium">{filteredReservations.filter((r: any) => r.hasInvoice).length} facturadas</span>
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/50 border-b">
                      <th className="px-3 py-2.5 w-8"></th>
                      <th className="px-4 py-2.5 text-left font-medium text-sereno-dark/60">Propiedad</th>
                      {ownerNamesRes.length > 1 && <th className="px-4 py-2.5 text-left font-medium text-sereno-dark/60">Propietario</th>}
                      <th className="px-4 py-2.5 text-left font-medium text-sereno-dark/60">Huésped</th>
                      <th className="px-4 py-2.5 text-left font-medium text-sereno-dark/60">DNI / Pasaporte</th>
                      <th className="px-4 py-2.5 text-left font-medium text-sereno-dark/60">Check-in</th>
                      <th className="px-4 py-2.5 text-left font-medium text-sereno-dark/60">Check-out</th>
                      <th className="px-4 py-2.5 text-right font-medium text-sereno-dark/60">Total</th>
                      <th className="px-4 py-2.5 text-right font-medium text-sereno-dark/60"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReservations.map((r: any) => {
                      const guest = r.guest;
                      const override = r.guestFormData;
                      const isOwnerRes = r.codigo?.startsWith('OWN') || r.huesped?.toLowerCase() === 'propietario';
                      const guestName = override?.guestName || (isOwnerRes ? 'Propietario' : (guest ? `${guest.name} ${guest.surname}`.trim() : r.huesped));
                      const guestDoc = override?.guestDocument || guest?.document_number || '';
                      const billing = r.billingFormData;
                      const isSelected = selectedIds.has(r.id);
                      return (
                        <tr key={r.id} className={`border-b last:border-0 transition-colors ${r.hasInvoice ? 'bg-green-50/30' : isSelected ? 'bg-sereno-beau/20' : 'hover:bg-gray-50/50'}`}>
                          <td className="px-3 py-3">
                            {!r.hasInvoice && (
                              <button onClick={() => toggleSelect(r.id)} className="text-sereno-dark/40 hover:text-sereno-dark transition-colors">
                                {isSelected ? <CheckSquare size={16} className="text-sereno-dark" /> : <Square size={16} />}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sereno-dark/80 text-xs font-medium">{r.anuncio}</td>
                          {ownerNamesRes.length > 1 && <td className="px-4 py-3 text-sereno-dark/60 text-xs">{r.ownerName}</td>}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <User size={14} className={guest ? 'text-green-500' : 'text-sereno-dark/20'} />
                              {guestName
                                ? <button onClick={() => openGuestModal(r)} className="text-sereno-dark font-medium hover:underline text-left">{guestName}</button>
                                : <button onClick={() => openGuestModal(r)} className="text-xs text-amber-500 hover:underline ml-0">Sin datos de check-in</button>}
                            </div>
                            {guest && !guestName && <button onClick={() => openGuestModal(r)} className="text-xs text-amber-500 hover:underline ml-5">Sin datos de check-in</button>}
                            {billing && (
                              <button onClick={() => openBillingModal(r)}
                                className="block ml-5 mt-0.5 text-xs text-blue-600 hover:underline text-left"
                                title="Se facturará con estos datos en vez de con los del check-in">
                                Factura a: {billing.billingName || billing.billingNif}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-sereno-dark/70">{billing?.billingNif || guestDoc || '—'}</td>
                          <td className="px-4 py-3 text-sereno-dark/70 text-xs">{formatDate(r.entrada)}</td>
                          <td className="px-4 py-3 text-sereno-dark/70 text-xs">{formatDate(r.salida)}</td>
                          <td className="px-4 py-3 text-right font-medium tabular-nums text-sereno-dark">{fmt(r.precioTotal)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {r.hasInvoice ? (
                                <button onClick={() => handleDownloadPdfRow(r)} disabled={downloadingPdfRow === r.id}
                                  className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-lg bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 transition-colors disabled:opacity-50">
                                  {downloadingPdfRow === r.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                                  Facturado
                                </button>
                              ) : (
                                <>
                                  <button onClick={() => openBillingModal(r)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                                      billing
                                        ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                                        : 'border-sereno-dark/25 text-sereno-dark/70 hover:bg-sereno-beau/20 hover:text-sereno-dark'
                                    }`}
                                    title="Facturar con datos de facturación distintos a los del check-in">
                                    <Pencil size={13} />
                                    Modificar
                                  </button>
                                  <button onClick={() => handleCreateInvoice(r)} disabled={!!creatingInvoice || bulkCreating || creatingFromModal}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-sereno-dark text-white hover:bg-sereno-dark/90 transition-colors disabled:opacity-50">
                                    {creatingInvoice === r.id ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                    Facturar
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y">
                {filteredReservations.map((r: any) => {
                  const guest = r.guest;
                  const override = r.guestFormData;
                  const isOwnerRes = r.codigo?.startsWith('OWN') || r.huesped?.toLowerCase() === 'propietario';
                  const guestName = override?.guestName || (isOwnerRes ? 'Propietario' : (guest ? `${guest.name} ${guest.surname}`.trim() : r.huesped));
                  const billing = r.billingFormData;
                  const isSelected = selectedIds.has(r.id);
                  return (
                    <div key={r.id} className={`p-4 space-y-2 ${r.hasInvoice ? 'bg-green-50/30' : isSelected ? 'bg-sereno-beau/20' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        {!r.hasInvoice && (
                          <button onClick={() => toggleSelect(r.id)} className="shrink-0 text-sereno-dark/40 hover:text-sereno-dark">
                            {isSelected ? <CheckSquare size={16} className="text-sereno-dark" /> : <Square size={16} />}
                          </button>
                        )}
                        <span className="text-sm font-medium text-sereno-dark flex-1">
                          {guestName
                            ? <button onClick={() => openGuestModal(r)} className="hover:underline text-left">{guestName}</button>
                            : <button onClick={() => openGuestModal(r)} className="text-amber-500 hover:underline text-left">Sin datos de check-in</button>}
                        </span>
                        <span className="font-medium tabular-nums text-sereno-dark">{fmt(r.precioTotal)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-sereno-dark/60">
                        <Building2 size={12} /> {r.anuncio}
                        {r.ownerName && <span className="ml-auto text-sereno-dark/40">{r.ownerName}</span>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-sereno-dark/50">
                        <Calendar size={12} /> {formatDate(r.entrada)} → {formatDate(r.salida)}
                      </div>
                      {billing && (
                        <div className="text-xs text-blue-600">Factura a: {billing.billingName || billing.billingNif}</div>
                      )}
                      {!guest && !guestName && null}
                      <div className="flex justify-end gap-1.5 pt-1">
                        {r.hasInvoice ? (
                          <button onClick={() => handleDownloadPdfRow(r)} disabled={downloadingPdfRow === r.id}
                            className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-lg bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 disabled:opacity-50">
                            {downloadingPdfRow === r.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                            Facturado
                          </button>
                        ) : (
                          <>
                            <button onClick={() => openBillingModal(r)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border ${
                                billing
                                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                                  : 'border-sereno-dark/25 text-sereno-dark/70'
                              }`}>
                              <Pencil size={13} />
                              Modificar
                            </button>
                            <button onClick={() => handleCreateInvoice(r)} disabled={!!creatingInvoice || bulkCreating || creatingFromModal}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-sereno-dark text-white hover:bg-sereno-dark/90 disabled:opacity-50">
                              {creatingInvoice === r.id ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                              Facturar
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {tab === 'facturas' && (
        <div className="space-y-4">
          {/* Bulk action bar */}
          {selectedInvoiceIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-sereno-beau/20 border border-sereno-beau/50 rounded-xl">
              <span className="text-sm font-medium text-sereno-dark">{selectedInvoiceIds.size} factura{selectedInvoiceIds.size !== 1 ? 's' : ''} seleccionada{selectedInvoiceIds.size !== 1 ? 's' : ''}</span>
              <button onClick={() => setSelectedInvoiceIds(new Set())} className="text-xs text-sereno-dark/50 hover:underline">Deseleccionar todo</button>
              {pushableCount > 0 && (
                <button onClick={handleBulkPushToHolded} disabled={bulkPushing || bulkDeletingInvoices}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sereno-dark text-white text-sm font-medium hover:bg-sereno-dark/90 disabled:opacity-50">
                  {bulkPushing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Enviar a Holded ({pushableCount})
                </button>
              )}
              <button onClick={handleBulkDeleteInvoices} disabled={bulkDeletingInvoices || bulkPushing}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-50 ${pushableCount > 0 ? '' : 'ml-auto'}`}>
                {bulkDeletingInvoices ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Eliminar seleccionadas
              </button>
              {pushProgress && (
                <div className="w-full space-y-1">
                  <div className="flex justify-between text-xs text-sereno-dark/50">
                    <span>Enviando a Holded…</span>
                    <span className="tabular-nums">{pushProgress.done} / {pushProgress.total}</span>
                  </div>
                  <div className="h-1.5 bg-sereno-beau/40 rounded-full overflow-hidden">
                    <div className="h-full bg-sereno-dark/60 rounded-full transition-all duration-200"
                      style={{ width: `${Math.round((pushProgress.done / pushProgress.total) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
              <input className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56" placeholder="Buscar huésped, nº factura…"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <select className="px-3 py-2 border rounded-lg text-sm" value={filterYear} onChange={e => setFilterYear(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="px-3 py-2 border rounded-lg text-sm" value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))}>
              <option value={0}>Todos los meses</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            {uniqueOwners.length > 1 && (
              <select className="px-3 py-2 border rounded-lg text-sm" value={filterOwner} onChange={e => setFilterOwner(e.target.value)}>
                <option value="">Todos los propietarios</option>
                {uniqueOwners.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
          </div>

          {loadingInvoices ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-sereno-dark/30" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-20 text-sereno-dark/40">
              <FileText size={48} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg">No hay facturas emitidas</p>
              <p className="text-sm mt-1">Ve a "Reservas a facturar" para generar facturas.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 w-8">
                        <button onClick={() => {
                          if (selectedInvoiceIds.size === filteredInvoices.length) {
                            setSelectedInvoiceIds(new Set());
                          } else {
                            setSelectedInvoiceIds(new Set(filteredInvoices.map((i: any) => i.id)));
                          }
                        }} className="text-sereno-dark/40 hover:text-sereno-dark">
                          {selectedInvoiceIds.size === filteredInvoices.length && filteredInvoices.length > 0
                            ? <CheckSquare size={16} />
                            : <Square size={16} />}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-sereno-dark/60">Nº Factura</th>
                      <th className="px-4 py-3 text-left font-medium text-sereno-dark/60">Fecha Checkout</th>
                      <th className="px-4 py-3 text-left font-medium text-sereno-dark/60">Huésped</th>
                      <th className="px-4 py-3 text-left font-medium text-sereno-dark/60">Propiedad</th>
                      <th className="px-4 py-3 text-left font-medium text-sereno-dark/60">Check-in / out</th>
                      <th className="px-4 py-3 text-right font-medium text-sereno-dark/60">Total</th>
                      <th className="px-4 py-3 text-left font-medium text-sereno-dark/60">Holded</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((inv: any) => (
                      <tr key={inv.id} className={`border-b last:border-0 hover:bg-gray-50/50 transition-colors ${selectedInvoiceIds.has(inv.id) ? 'bg-sereno-beau/20' : ''}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => setSelectedInvoiceIds(prev => {
                            const s = new Set(prev);
                            s.has(inv.id) ? s.delete(inv.id) : s.add(inv.id);
                            return s;
                          })} className="text-sereno-dark/40 hover:text-sereno-dark">
                            {selectedInvoiceIds.has(inv.id) ? <CheckSquare size={16} className="text-sereno-dark" /> : <Square size={16} />}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-medium text-sereno-dark">{inv.invoiceNumber}</td>
                        <td className="px-4 py-3 text-sereno-dark/70">{formatDate(inv.invoiceDate)}</td>
                        <td className="px-4 py-3 text-sereno-dark font-medium">
                          {inv.guestName}
                          {hasCustomBilling(inv) && (
                            <span className="block text-xs font-normal text-blue-600" title="Emitida con datos de facturación del cliente">
                              Factura a: {invoiceRecipient(inv).name}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sereno-dark/70">{inv.propertyNickname}</td>
                        <td className="px-4 py-3 text-sereno-dark/60 text-xs">{formatDate(inv.checkIn)} → {formatDate(inv.checkOut)}</td>
                        <td className="px-4 py-3 text-right font-medium tabular-nums text-sereno-dark">{fmt(inv.totalAmount)}</td>
                        <td className="px-4 py-3">
                          <HoldedCell inv={inv} onPush={handlePushToHolded}
                            pushing={pushingHolded === inv.id} disabled={bulkPushing} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setSelectedInvoice(inv)} className="p-1.5 rounded hover:bg-sereno-beau/30 text-sereno-dark/50 hover:text-sereno-dark"><Eye size={16} /></button>
                            <button onClick={() => handleDownloadPdf(inv)} disabled={downloadingPdf === inv.id} className="p-1.5 rounded hover:bg-sereno-beau/30 text-sereno-dark/50 hover:text-sereno-dark">
                              {downloadingPdf === inv.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                            </button>
                            <button onClick={() => handleDeleteInvoice(inv)} className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/30 hover:text-red-600"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y">
                {filteredInvoices.map((inv: any) => (
                  <div key={inv.id} className={`p-4 space-y-2 ${selectedInvoiceIds.has(inv.id) ? 'bg-sereno-beau/20' : ''}`}>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedInvoiceIds(prev => {
                        const s = new Set(prev);
                        s.has(inv.id) ? s.delete(inv.id) : s.add(inv.id);
                        return s;
                      })} className="text-sereno-dark/40 hover:text-sereno-dark flex-shrink-0">
                        {selectedInvoiceIds.has(inv.id) ? <CheckSquare size={16} className="text-sereno-dark" /> : <Square size={16} />}
                      </button>
                      <span className="font-mono text-xs font-medium text-sereno-dark">{inv.invoiceNumber}</span>
                      <span className="font-medium tabular-nums text-sereno-dark">{fmt(inv.totalAmount)}</span>
                    </div>
                    <div className="text-sm font-medium text-sereno-dark">{inv.guestName}</div>
                    {hasCustomBilling(inv) && (
                      <div className="text-xs text-blue-600">Factura a: {invoiceRecipient(inv).name}</div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-sereno-dark/60">
                      <Building2 size={12} /> {inv.propertyNickname}
                      <span className="ml-auto">{formatDate(inv.checkIn)} → {formatDate(inv.checkOut)}</span>
                    </div>
                    <div className="flex items-center gap-1 pt-1">
                      <HoldedCell inv={inv} onPush={handlePushToHolded}
                        pushing={pushingHolded === inv.id} disabled={bulkPushing} />
                      <button onClick={() => setSelectedInvoice(inv)} className="ml-auto p-1.5 rounded hover:bg-sereno-beau/30"><Eye size={16} /></button>
                      <button onClick={() => handleDownloadPdf(inv)} className="p-1.5 rounded hover:bg-sereno-beau/30"><Download size={16} /></button>
                      <button onClick={() => handleDeleteInvoice(inv)} className="p-1.5 rounded hover:bg-red-50 text-red-400"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {guestModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setGuestModal(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-sereno-dark mb-1">Datos del huésped</h3>
            <p className="text-xs text-sereno-dark/50 mb-4">{guestModal.reservation.anuncio} · {formatDate(guestModal.reservation.entrada)} → {formatDate(guestModal.reservation.salida)}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-sereno-dark mb-1">Nombre completo</label>
                <input className="w-full px-3 py-2 border rounded-lg text-sm" value={guestForm.guestName}
                  onChange={e => setGuestForm(f => ({ ...f, guestName: e.target.value }))} placeholder="Nombre y apellidos" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-sereno-dark mb-1">Tipo documento</label>
                  <select className="w-full px-3 py-2 border rounded-lg text-sm" value={guestForm.guestDocumentType}
                    onChange={e => setGuestForm(f => ({ ...f, guestDocumentType: e.target.value }))}>
                    <option value="DNI">DNI</option>
                    <option value="NIE">NIE</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-sereno-dark mb-1">Nº documento</label>
                  <input className="w-full px-3 py-2 border rounded-lg text-sm font-mono" value={guestForm.guestDocument}
                    onChange={e => setGuestForm(f => ({ ...f, guestDocument: e.target.value }))} placeholder="12345678A" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-sereno-dark mb-1">Dirección</label>
                <input className="w-full px-3 py-2 border rounded-lg text-sm" value={guestForm.guestAddress}
                  onChange={e => setGuestForm(f => ({ ...f, guestAddress: e.target.value }))} placeholder="Calle, ciudad, país" />
              </div>
              <div>
                <label className="block text-sm font-medium text-sereno-dark mb-1">Nacionalidad</label>
                <input className="w-full px-3 py-2 border rounded-lg text-sm" value={guestForm.guestNationality}
                  onChange={e => setGuestForm(f => ({ ...f, guestNationality: e.target.value }))} placeholder="España" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setGuestModal(null)} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancelar</button>
              <button onClick={saveGuestData} className="px-4 py-2 text-sm rounded-lg border border-sereno-dark/30 hover:bg-sereno-beau/20 text-sereno-dark">Guardar</button>
              {!guestModal.reservation.hasInvoice && (
                <button onClick={handleGuestModalInvoice} disabled={creatingFromModal || !!creatingInvoice}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-sereno-dark text-white hover:bg-sereno-dark/90 disabled:opacity-50">
                  {creatingFromModal ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  Guardar y Facturar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {billingModal && (() => {
        const r = billingModal.reservation;
        const actuales = checkinData(r);
        const esEmpresa = billingForm.billingIsCompany;
        const faltan = [
          !billingForm.billingName.trim() && (esEmpresa ? 'razón social' : 'nombre'),
          !billingForm.billingNif.trim() && 'NIF/CIF',
          !billingForm.billingAddress.trim() && 'dirección fiscal',
        ].filter(Boolean) as string[];
        // Empezado a rellenar pero incompleto: hay que avisar de lo que falta
        const empezado = faltan.length < 3 || Boolean(billingForm.billingEmail.trim());
        const completo = faltan.length === 0;
        return (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setBillingModal(null)}>
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xl my-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-sereno-dark mb-1">Datos de facturación</h3>
              <p className="text-xs text-sereno-dark/50 mb-4">
                {r.anuncio} · {formatDate(r.entrada)} → {formatDate(r.salida)}
              </p>

              <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5 mb-4 text-xs text-sereno-dark/60">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-sereno-dark/70">Datos del check-in</span>
                  <button onClick={copyCheckinIntoBilling} className="text-blue-600 hover:underline">Copiar abajo</button>
                </div>
                <div>{actuales.guestName || '—'}</div>
                <div className="font-mono">{actuales.guestDocument || 'Sin documento'}</div>
                {actuales.guestAddress && <div>{actuales.guestAddress}</div>}
              </div>

              <p className="text-sm text-sereno-dark/60 mb-3">
                Rellena aquí los datos que ha dado el cliente. La factura se emitirá a este nombre y el huésped
                quedará como dato de la estancia. Los campos marcados con <span className="text-red-500">*</span> son
                obligatorios para poder facturar a otro nombre.
              </p>

              <div className="space-y-3">
                <div className="flex gap-2">
                  {[{ v: true, label: 'Empresa' }, { v: false, label: 'Particular' }].map(opt => (
                    <button key={String(opt.v)}
                      onClick={() => setBillingForm(f => ({
                        ...f,
                        billingIsCompany: opt.v,
                        // El tipo de documento por defecto cambia con el tipo de cliente,
                        // salvo que ya se haya elegido otro a mano
                        billingNifType: (f.billingNifType === 'CIF' || f.billingNifType === 'DNI')
                          ? (opt.v ? 'CIF' : 'DNI') : f.billingNifType,
                      }))}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        esEmpresa === opt.v
                          ? 'border-sereno-dark bg-sereno-dark text-white'
                          : 'border-gray-300 text-sereno-dark/70 hover:bg-gray-50'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium text-sereno-dark mb-1">
                    {esEmpresa ? 'Razón social' : 'Nombre completo'} <span className="text-red-500">*</span>
                  </label>
                  <input className="w-full px-3 py-2 border rounded-lg text-sm" value={billingForm.billingName}
                    onChange={e => setBillingForm(f => ({ ...f, billingName: e.target.value }))}
                    placeholder={esEmpresa ? 'Empresa S.L.' : 'Nombre y apellidos'} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-sereno-dark mb-1">Tipo documento</label>
                    <select className="w-full px-3 py-2 border rounded-lg text-sm" value={billingForm.billingNifType}
                      onChange={e => setBillingForm(f => ({ ...f, billingNifType: e.target.value }))}>
                      <option value="CIF">CIF</option>
                      <option value="DNI">DNI</option>
                      <option value="NIE">NIE</option>
                      <option value="Pasaporte">Pasaporte</option>
                      <option value="VAT">VAT (intracomunitario)</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-sereno-dark mb-1">NIF / CIF <span className="text-red-500">*</span></label>
                    <input className="w-full px-3 py-2 border rounded-lg text-sm font-mono" value={billingForm.billingNif}
                      onChange={e => setBillingForm(f => ({ ...f, billingNif: e.target.value }))}
                      placeholder={esEmpresa ? 'B12345678' : '12345678A'} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-sereno-dark mb-1">Dirección fiscal <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border rounded-lg text-sm" value={billingForm.billingAddress}
                    onChange={e => setBillingForm(f => ({ ...f, billingAddress: e.target.value }))}
                    placeholder="Calle, nº, CP, ciudad, país" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sereno-dark mb-1">Email <span className="font-normal text-sereno-dark/40">(opcional)</span></label>
                  <input type="email" className="w-full px-3 py-2 border rounded-lg text-sm" value={billingForm.billingEmail}
                    onChange={e => setBillingForm(f => ({ ...f, billingEmail: e.target.value }))}
                    placeholder="facturacion@empresa.com" />
                </div>
              </div>

              {empezado && !completo && (
                <p className="flex items-start gap-1.5 mt-3 text-xs text-amber-700">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" />
                  Falta {faltan.join(', ').replace(/, ([^,]*)$/, ' y $1')}. Sin los tres datos no se puede emitir
                  la factura a otro nombre.
                </p>
              )}

              <div className="flex flex-wrap justify-end gap-2 mt-6">
                {r.billingFormData && (
                  <button onClick={clearBillingData} className="mr-auto px-4 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                    Quitar datos
                  </button>
                )}
                <button onClick={() => setBillingModal(null)} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancelar</button>
                <button onClick={saveBillingData} disabled={!completo}
                  className="px-4 py-2 text-sm rounded-lg border border-sereno-dark/30 hover:bg-sereno-beau/20 text-sereno-dark disabled:opacity-40">
                  Guardar
                </button>
                <button onClick={handleBillingModalInvoice} disabled={!completo || creatingFromModal || !!creatingInvoice}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-sereno-dark text-white hover:bg-sereno-dark/90 disabled:opacity-50">
                  {creatingFromModal ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  Guardar y facturar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showSeriesSettings && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowSeriesSettings(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-sereno-dark mb-4">Serie de facturación</h3>
            <p className="text-sm text-sereno-dark/60 mb-4">Serie unificada para todas las facturas a huéspedes.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-sereno-dark mb-1">Prefijo</label>
                <input className="w-full px-3 py-2 border rounded-lg text-sm" value={seriesPrefix} onChange={e => setSeriesPrefix(e.target.value)} placeholder="F-2026-" />
                <p className="text-xs text-sereno-dark/40 mt-1">Ejemplo: F-2026- → F-2026-0001, F-2026-0002…</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-sereno-dark mb-1">Próximo número</label>
                <input type="number" className="w-full px-3 py-2 border rounded-lg text-sm" value={seriesNextNum} onChange={e => setSeriesNextNum(e.target.value)} min={1} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowSeriesSettings(false)} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSaveSeries} disabled={savingSeries} className="px-4 py-2 text-sm rounded-lg bg-sereno-dark text-white hover:bg-sereno-dark/90 disabled:opacity-50">
                {savingSeries ? <Loader2 size={16} className="animate-spin" /> : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
